import React, { useEffect, useState, useContext, useRef  } from "react";
import { useSelector } from "react-redux";

import { Box, IconButton, Tooltip, Zoom, Checkbox, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import * as _ from "lodash";
import debounce from "lodash/debounce";

import "./Voyage.scss";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { BUTTONS, Button, COLORS, ICONS, Icon, Modal } from "abs-ui-components";

import axiosHttp from "../../../../services/interceptor";
import { emitToasterMsg } from "../../../../services/toasterUtility";
import AbsModal from "../../../../common/absModal/AbsModal";
import { accessAPIForResponse } from "../../../../services/api-services/api-implementations";
import VoyageForm from "./VoyageForm/VoyageForm";
import { VOYAGE_HEADER } from "./VoyageConstants";
import ImportAdditionalDataForm from "../../ImportAdditionalDataForm/ImportAdditionalDataForm";
import { ClaimsContext } from "../../../../services/ClaimsService";
import { ClaimType } from "../../../../constants/claims";
import { FeatureType } from "../../../../constants/FeatureToggles";
import webhookQueueService from "../../../../services/WebhookQueueService";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";


const BASE_URL = process.env.REACT_APP_BASE_URL;

const Voyage = ({ showData }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const { userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
	let currentTime = new Date().toISOString();
	const reportDetails = useSelector(
		(state) => state.reports.selectedReportDetails
	);
	const [voyageConfirmDelete, setVoyageConfirmDelete] = useState(false);
	const [voaygeData, setVoyageData] = useState([]);
	const [rowSelection, setRowSelection] = useState({});
	const [openFullYearModal, setOpenFullYearModal] = useState(false);
	const [isGenericErrorsModalOpen, setIsGenericErrorsModalOpen] =
		useState(false);
	const [isDataValidated, setIsDataValidated] = useState(false);
	const [validationMessages, setValidationMessages] = useState([]);
	const [validateTypes, setValidateTypes] = useState([]);
	const [globalErrors, setGlobalErrors] = useState([]);
	const [isAnyMisStatementErrors, setIsAnyMisStatementErrors] =
		useState(false);
	const [isMisStatementCalled, setIsMisStatementCalled] = useState(true);
	const [voyagesEnabled, setVoyagesEnabled] = useState(false);
	const [isCorrectionReqModalOpen, setIsCorrectionReqModalOpen] =
		useState(false);
	const [type, setType] = useState("Add");
	const [editVoyage, setEditVoyage] = useState(null);
	const [selectAllCheckboxes, setSelectAllCheckboxes] = useState(false);
	const [rowSelections, setRowSelections] = useState([]);
	const [disableBtn, setDisableBtn] = useState(false);
	const [isImportAdditionalDataOpen, setIsImportAdditionalDataOpen] =
		useState(false);
	const [sampleVoyages, setSampleVoyages] = useState([]);
	const [exportVoyageEnabled, setExportVoyageEnabled] = useState(true);
	const [voyageFields, setVoyageFields] = useState([]);
	const [isVoyageFormLoading, setIsVoyageFormLoading] = useState(false);

	const showMisFilters = userFeaturesAreLoaded && userHasFeature(FeatureType.ShowMisFilters);

	//iframe
	const [showbreadCrumb, setShowBreadCrumb] = useState(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		return storedValue !== null ? JSON.parse(storedValue) : true;
	});

	// Create a debounced setter function
	const debouncedSetHeaderFooterEnabled = debounce((value) => {
		setShowBreadCrumb(value);
	}, 100);

	useEffect(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		if (storedValue !== null) {
			debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
		}
	}, [debouncedSetHeaderFooterEnabled]);

	useEffect(() => {
		getVoyageData();
		getIsAnyMisStatementError();

		// Re-attach callbacks for any in-progress MIS validation after page refresh
		const state = webhookQueueService.getState();
		const inProgressOp = state.queue.find(
			(op) => op.label === "MIS Error Validation" && op.status === "processing"
		);
		if (inProgressOp?.webhookId) {
			beginLoad();
			webhookQueueService.reattachCallbacks(inProgressOp.webhookId, {
				onComplete: async (result) => {
					try {
						await getVoyageData();
						await getIsAnyMisStatementError();
					} catch (error) {
						console.error(error);
					} finally {
						endLoad();
					}
				},
				onError: (error) => {
					console.error(error);
					endLoad();
				},
			});
		}
	}, []);

	useEffect(() => {
		setDisableBtn(false);
	}, [isCorrectionReqModalOpen]);

	// Loading state management
	const [pendingLoads, setPendingLoads] = useState(0);
	const beginLoad = () => setPendingLoads((n) => n + 1);
	const endLoad = () => setPendingLoads((n) => Math.max(0, n - 1));

	const getVoyageFormData = async () => {
		beginLoad();
		try {
			const response = await axiosHttp.get(
				`api/Voyages/GetVoyageForm?vesselMstId=${reportDetails.VesselMstId}&reportYear=${reportDetails.ReportYear}&reportMstId=${reportDetails.ReportMstId}`
			);
			setVoyageFields(response?.data);
			setIsCorrectionReqModalOpen(true);
		} catch (error) {
			console.error(error);
		} finally {
			endLoad();
		}
	};

	const toggleCorrectionRequest = () => {
		setEditVoyage(null);
		setType("Add");
		getVoyageFormData();
	};
	const closeForm = () => {
		setIsCorrectionReqModalOpen(false);
	};

	const getVoyageData = async () => {
		beginLoad();
		try {
			const response = await axiosHttp({
				url: `api/Voyages/GetMepcVoyages?cacheburst=${currentTime}&reportMstId=${reportDetails.ReportMstId}`,
				method: "GET",
			});
			const responseData = response?.data?.slice(1, -2);
			const showValidationMessages =
				response?.data?.find(
					(item) => item.headerName === "VALIDATION MESSAGES"
				)?.values || {};
			const validationTypes =
				response?.data?.find(
					(item) => item.headerName === "VALIDATION TYPE"
				)?.values || {};
			const isSampleVoyages =
				response?.data?.find(
					(item) => item.headerName === "IS SAMPLE VOYAGE"
				)?.values || {};
			if (responseData) {
				setVoyageData(responseData || []);
				setValidationMessages(showValidationMessages);
				setValidateTypes(validationTypes);
				setSampleVoyages(isSampleVoyages);
				await getGlobalError();
				const dataValidated = responseData.some(
					(ele) => ele.IsValidated
				);
				setIsDataValidated(dataValidated);
			}
		} catch (error) {
			setVoyageData([]);
		} finally {
			endLoad();
		}
	};

	const onPatchEditVoyage = async (id) => {
		setType("Edit");
		setIsCorrectionReqModalOpen(true);
		const voyageDtlId = id.split("|")[1];
		setIsVoyageFormLoading(true);
		try {
			const response = await axiosHttp.get(
				`api/Voyages/GetVoyageForm?vesselMstId=${reportDetails.VesselMstId}&voyageDtlId=${voyageDtlId}&reportYear=${reportDetails.ReportYear}&reportMstId=${reportDetails.ReportMstId}`
			);
			setVoyageFields(response?.data);
		} catch (error) {
			console.error(error);
		} finally {
			setIsVoyageFormLoading(false);
		}
	};

	const handleDeletingVoyageRecord = async (selectedDeletVoaygeRows) => {
		const payload = {
			voyageDtlIds: selectedDeletVoaygeRows,
		};
		beginLoad();
		try {
			const res = await axiosHttp({
				url: `api/Voyages/DeleteVoyage`,
				method: "DELETE",
				data: payload,
			});
			if (res && res?.data.isSuccess) {
				setVoyageConfirmDelete(false);
				await getVoyageData();
				await getIsAnyMisStatementError();
				emitToasterMsg("success", "Voyage", "Deleted Successfully");
				setRowSelection({});
			}
		} catch (error) {
			setVoyageConfirmDelete(false);
			console.error(error);
		} finally {
			endLoad();
		}
	};

	const getGlobalError = async () => {
		beginLoad();
		try {
			const res = await accessAPIForResponse("GetGlobalErrors", null, [
				reportDetails.ReportMstId,
			]);
			setGlobalErrors(res);
		} catch (error) {
			console.error(error);
		} finally {
			endLoad();
		}
	};

	const handleFullYearModalClose = () => {
		if (openFullYearModal) {
			setOpenFullYearModal(false);
		}
	};

	const sendValidateApiRequest = async () => {
		const isWebhookPanelVisible = userFeaturesAreLoaded && userHasFeature(FeatureType.ViewWebhookPanel);

		beginLoad();
		try {
			webhookQueueService.enqueue(
				() =>
					axiosHttp({
						url: `api/MisstatementValidation/EnqueueMisstatementValidation?reportMstId=${reportDetails.ReportMstId}`,
						method: "POST",
					}),
				{
					label: "MIS Error Validation",
					description: "Validating MIS errors for the report",
					silent: !isWebhookPanelVisible,
					onComplete: async (result) => {
						try {
							await getVoyageData();
							await getIsAnyMisStatementError();
						} catch (error) {
							console.error(error);
						} finally {
							endLoad();
						}
					},
					onError: (error) => {
						console.error(error);
						endLoad();
					},
				}
			);
		} catch (error) {
			console.error(error);
			endLoad();
		}
	};

	const toggleGenericErrors = () => {
		setIsGenericErrorsModalOpen(!isGenericErrorsModalOpen);
	};

	const handleExportVoyageTable = async () => {
		setExportVoyageEnabled(false);
		beginLoad();
		try {
			const response = await accessAPIForResponse("GetVoyagesExcel", null, [
				reportDetails.ReportMstId,
			]);
			if (response) {
				const contentType = response.type;
				if (
					contentType ===
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				) {
					const blob = new Blob([response], {
						type: contentType,
					});
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", "Voyage Export.xlsx");
					document.body.appendChild(link);
					link.click();
					emitToasterMsg("success", "Exported successfully");
					document.body.removeChild(link);
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setExportVoyageEnabled(true);
			endLoad();
		}
	};

	const getIsAnyMisStatementError = async () => {
		beginLoad();
		try {
			const response = await accessAPIForResponse(
				"GetIsAnyMisStatementErrors",
				null,
				[reportDetails.ReportMstId]
			);

			
			if (response) {
				setIsAnyMisStatementErrors(response.IsAnyMisStatementError);
				setIsMisStatementCalled(response.IsMisStatementCalled);
				setVoyagesEnabled(response.VoyagesEnabled);
			}
		} catch (error) {
			console.error(error);
		} finally {
			endLoad();
		}
	};

	const toggleImportAdditionalData = (needRefetch = false) => {
		setIsImportAdditionalDataOpen(!isImportAdditionalDataOpen);
		if (typeof needRefetch == "boolean" && needRefetch) {
			getVoyageData();
		}
	};

	const refetchVoyagesAndMisStatementErrors = () => {
		getVoyageData();
		getIsAnyMisStatementError();
	};

	useEffect(() => {
		setRowSelections(voaygeData.map(() => false)); // Default all to unchecked
	}, [voaygeData]);

	const handleRowCheckboxChange = (index) => (event) => {
		const isChecked = event.target.checked;

		const updatedSelections = [...rowSelections];
		updatedSelections[index] = isChecked;
		setRowSelections(updatedSelections);

		if (!isChecked) {
			setSelectAllCheckboxes(false);
		} else if (updatedSelections.every(Boolean)) {
			setSelectAllCheckboxes(true);
		}
	};
	const footerData = voaygeData.reduce((acc, item) => {
		if (item.values.hasOwnProperty("0")) {
			acc.push(item.values["0"]);
		}
		return acc;
	}, []);

	const getWidthStyle = (headerName) => {
		switch (headerName) {
			case "IS SAMPLE VOYAGE":
				return {
					left: voyagesEnabled ? "120px" : "0px",
				};
			case "TIME STAMP":
				return {
					minWidth: "200px",
					position: "relative",
					left: voyagesEnabled ? "360px" : "120px",
				};
			case "EVENT TYPE":
				return {
					left: voyagesEnabled ? "560px" : "320px",
				};
			default:
				return { width: "auto", position: "relative" };
		}
	};

	const getValidationBg = (validationTypeName, rowIndex) => {
		switch (validationTypeName) {
			case "ERROR":
				return {
					backgroundColor: "rgb(248, 128, 125)",
		            top: `${53 * rowIndex}px`,
				};
			case "WARNING":
				return {
					backgroundColor: "#fada6c",
					top: `${53 * rowIndex}px`,
				};
			case "EXCLUSION":
				return {
					backgroundColor: "#8ec7fd",
					top: `${53 * rowIndex}px`,
				};
			default:
				return {
					backgroundColor: "rgb(255, 255, 255)",
					top: `${53 * rowIndex}px`,
				};
		}
	};

	const getRowStyle = (rowKey) => {
		const validationType = validateTypes[rowKey];
		switch (validationType) {
			case "ERROR":
				return { backgroundColor: "#f66764" };
			case "WARNING":
				return { backgroundColor: "#fada6c" };
			case "EXCLUSION":
				return { backgroundColor: "#8ec7fd" };
			default:
				return { className: "row-default" };
		}
	};

	const countTotalRows = (data) => {
		if (data && data.length > 0) {
			const values = data[0].values;
			if (values) {
				const filteredKeys = Object.keys(values).filter(
					(key) => key !== "0"
				);
				return filteredKeys.length;
			} else {
				return 0;
			}
		} else {
			return 0;
		}
	};

	const totalRows = countTotalRows(voaygeData);

	const selectedRowCount = Object.keys(rowSelections).filter(
		(key) => rowSelections[key]
	).length;

	const handleClearSelection = () => {
		setRowSelections([]);
		setSelectAllCheckboxes(false);
	};

	const downloadErrorsExcel = async () => {
		await axiosHttp
			.get(`api/Debugger/DebugGlobalErrors?reportMstId=${reportDetails.ReportMstId}`, {
				responseType: "blob"
			})
			.then((response) => {

				const url = window.URL.createObjectURL(new Blob([response.data]));

				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", "GlobalErrors.xlsx");

				document.body.appendChild(link);
				link.click();

				link.remove();
				window.URL.revokeObjectURL(url);
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const showMis15DebugLogs = userHasFeature(FeatureType.Mis15DebugLogs);

	//Mis UI Design 
    const [filter, setFilter] = useState("ALL"); // ALL | ERROR | WARNING
	const [currentErrorIndex, setCurrentErrorIndex] = useState(0);
    

	const FILTERS = {
		ALL: "ALL",
		ERROR: "ERROR",
		WARNING: "WARNING",
	};

	const allRowKeys = Object.keys(voaygeData[0]?.values || {}).filter((key) => key !== "0");
   
	const filteredRowKeys = allRowKeys.filter((key) => {
		if (filter === "ERROR") return validateTypes[key] === "ERROR";
		if (filter === "WARNING") return validateTypes[key] === "WARNING";
		return true; // ALL
	});
    
	const errorKeys = allRowKeys.filter((key) => validateTypes[key] === "ERROR");

    const [activeRowKey, setActiveRowKey] = useState(null);

	const previousErrorIndexRef = useRef(-1);

	const activeErrorIndex = errorKeys.indexOf(activeRowKey);

	useEffect(() => {
		if (activeErrorIndex !== -1) {
			previousErrorIndexRef.current = activeErrorIndex;
		}
	}, [activeErrorIndex]);

	
	const warningKeys = allRowKeys.filter((key) => validateTypes[key] === "WARNING");

    const hasNoIssues = errorKeys.length === 0 && warningKeys.length === 0;
    
	useEffect(() => {
		if (filter === FILTERS.ERROR && errorKeys.length === 0) {
			setFilter(FILTERS.ALL);
			setCurrentErrorIndex(-1);
			setActiveRowKey(null);
		}

		if (filter === FILTERS.WARNING && warningKeys.length === 0) {
			setFilter(FILTERS.ALL);
			setActiveRowKey(null);
		}
	}, [filter, errorKeys.length, warningKeys.length]);


	const isEmptyFilteredRows = filteredRowKeys.length === 0;

	const showErrorNavigation = errorKeys.length > 0 && filter !== FILTERS.WARNING;;


	useEffect(() => {
		if (errorKeys.length === 0) {
			setActiveRowKey(null);
			return;
		}

		if (activeRowKey && !errorKeys.includes(activeRowKey)) {
			const previousIndex = previousErrorIndexRef.current;

			const nextIndex = Math.min(previousIndex, errorKeys.length - 1);

			const nextKey = errorKeys[nextIndex];

			if (nextKey) {
				scrollToError(nextKey);
			}
		}
	}, [errorKeys, activeRowKey]);

    const onSelectOnlyErrors = () => {
		setFilter("ERROR");
		setCurrentErrorIndex(0);
    };

    const onSelectOnlyWarnings = () => {
		setFilter(FILTERS.WARNING);
    };

    const onClearFilter = () => {
		setFilter(FILTERS.ALL);
		setCurrentErrorIndex(-1);
		setActiveRowKey(null);
    };

    const scrollToError = (key) => {
		setActiveRowKey(key);

		const element = document.getElementById(`row-${key}`);
		const tableContainer = document.querySelector(".voyage-table");

		if (element && tableContainer) {
			const containerTop = tableContainer.scrollTop;
			const containerHeight = tableContainer.clientHeight;

			const elementTop = element.offsetTop;
			const elementHeight = element.clientHeight;

			const scrollPosition = elementTop - containerHeight / 2 + elementHeight / 2;

			tableContainer.scrollTo({
				top: scrollPosition,
				behavior: "smooth",
			});
		}
	};

    const handleNextError = () => {
		if (errorKeys.length === 0) return;

		if (!activeRowKey) {
			scrollToError(errorKeys[0]);
			return;
		}

		const currentIndex = errorKeys.indexOf(activeRowKey);

		if (currentIndex < errorKeys.length - 1) {
			scrollToError(errorKeys[currentIndex + 1]);
		}
	};

    const handlePrevError = () => {
		if (errorKeys.length === 0 || !activeRowKey) return;

		const currentIndex = errorKeys.indexOf(activeRowKey);

		if (currentIndex > 0) {
			scrollToError(errorKeys[currentIndex - 1]);
		}
	};  
	return (
		<>
			{/* Main voyage page container */}
			<div className="total-voyage">
				{/* Top action toolbar: Add, Import, Export, Delete, Validate, Global Errors */}
				<div className="d-flex justify-content-between fixed-action-btns pr-1 align-items-center mb-3">
					{/* Left-side actions (Add, Import, Export, Delete) */}
					<div>
						<div className="mt-4 d-flex justify-content-center align-items-center">
							{voyagesEnabled && showbreadCrumb && (
								<Button
									onClick={toggleCorrectionRequest}
									startIcon={<Icon icon={ICONS.ADD} />}
									className="ml-2"
								>
									Add New Voyage
								</Button>
							)}
							{userHasClaim(ClaimType.SHOW_IMPORT_ADDITIONAL_DATA) &&
								showbreadCrumb &&
								true && (
									<Button
										startIcon={
											<Icon icon={"exit_to_app"} />
										}
										className="ml-2"
										onClick={toggleImportAdditionalData}
									>
										Import Additional Data
									</Button>
								)}
							<Button
								startIcon={<Icon icon="ios_share" />}
								onClick={handleExportVoyageTable}
								variant="secondary"
								className="ml-2"
								disabled={!exportVoyageEnabled}
							>
								{exportVoyageEnabled
									? "Export Voyages"
									: "Please wait..."}
							</Button>
							{voyagesEnabled && showbreadCrumb && (
								<Button
									onClick={() => setVoyageConfirmDelete(true)}
									primaryBackgroundColor={COLORS.RED.DARK}
									primaryColor={COLORS.RED.DARK}
									disabled={rowSelections.every(
										(selection) => !selection
									)}
									startIcon={<Icon icon="delete" />}
									className="ml-2 delete-btn"
								>
									Delete
								</Button>
							)}
						</div>
					</div>				
				</div>

           {/* Action toolbar: Validation, Global Errors, MIS Logs, Filter, Navigation */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: 12,
						marginBottom: 10,
					}}
				>
					<div
						className="validation-toolbar"
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: 12,
							marginBottom: 10,
							gap: "12px",
						}}
					>
						{/* Warnings */}
						{showMisFilters && warningKeys.length > 0 && (
							<Button
								className="validate-btn warning-btn"
								primaryColor="rgb(250,218,108)"
								primaryBackgroundColor="rgb(245, 205, 85)"
								onClick={onSelectOnlyWarnings}
								startIcon={filter === FILTERS.WARNING ? <FilterAltIcon style={{fontSize: "16px",}}/> : null}
							>
								<span >Warning(s)</span>

								<span className="filter-count-badge">{warningKeys.length}</span>
							</Button>
						)}

						{/* Errors */}
						{showMisFilters && errorKeys.length > 0 && (
							<Button
								className="validate-btn"
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
								onClick={onSelectOnlyErrors}
								startIcon={filter === FILTERS.ERROR ? <FilterAltIcon style={{fontSize: "16px",}}/> : null}
							>
								<span>Error(s)</span>

								<span className="filter-count-badge">{errorKeys.length}</span>
							</Button>
						)}

						{/* Clear */}
						{showMisFilters && filter !== FILTERS.ALL && (
							<Button className="validate-btn" primaryColor="#012c51" onClick={onClearFilter}>
								Clear
							</Button>
						)}

						{/* Navigation */}
						{showMisFilters && showErrorNavigation && (
							<span
								style={{
									display: "flex",
									alignItems: "center",
									gap: "6px",
								}}
							>
								<Button
									onClick={handlePrevError}
									disabled={!activeRowKey || activeErrorIndex <= 0}
									variant="secondary"
									className="navigation-btn"
									startIcon={<ChevronLeftIcon />}
								>
									Previous Error
								</Button>

								<Button
									onClick={handleNextError}
									disabled={errorKeys.length === 0 || (activeRowKey && activeErrorIndex >= errorKeys.length - 1)}
									variant="secondary"
									className="navigation-btn"
									endIcon={<ChevronRightIcon />}
								>
									Next Error
								</Button>
							</span>
						)}
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: 12,
							marginBottom: 10,
							gap: "12px",
						}}
					>
						{globalErrors.length > 0 && (
							<>
								{/* Download MIS-15 Logs */}
								{showMis15DebugLogs && (
									<Button
										onClick={downloadErrorsExcel}
										variant="secondary"
										className="navigation-btn"
										startIcon={<Icon icon={ICONS.DOWNLOAD} />}
									>
										Download MIS-15 Logs
									</Button>
								)}

								{/* Global Errors */}
								<Button
									onClick={toggleGenericErrors}
									disabled={globalErrors.length === 0}
									variant="secondary"
									className="navigation-btn"
									startIcon={<Icon icon={ICONS.WARNING_AMBER} />}
								>
									Global Errors
								</Button>
							</>
						)}

						{/* Validate */}
						{showData && (
							
							<>
								{isMisStatementCalled &&
									!isAnyMisStatementErrors ? (
									""
								) : (
							<Button
								className="validate-btn"
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
								onClick={() => sendValidateApiRequest(false)}
							>
								Validate
							</Button>)
                            }
							</>
						)}
					</div>
				</div>


				{/* Sticky row selection summary header */}
				{selectedRowCount > 0 ? (
					<div className="row-selection-header mb-0 p-0">
						<div className="row-selection-info">
							<span>{`${selectedRowCount} of ${totalRows} row(s) selected`}</span>
							<button
								className="clear-selection-btn"
								onClick={handleClearSelection}
							>
								CLEAR SELECTION
							</button>
						</div>
					</div>
				) : null}

				{/* Table section */}
				<div
					className="voyage-table-wrapper"
					aria-busy={pendingLoads > 0}
					aria-live="polite"
					style={{ position: "relative" }}
				>
					{/* Table container */}
					<div className={`voyage-table`} style={{ position: "relative" }}>
						<div
							className={
								voyagesEnabled ? "enable-edit" : "disable-edit"
							}
						>
							{/* Voyage data table */}
							<table className="table">
								<thead>
									<tr>
										{voyagesEnabled && (
											<th style={{ left: "0" }}></th>
										)}
										<th
											style={getWidthStyle(
												"IS SAMPLE VOYAGE"
											)}
										>
											IS SAMPLE VOYAGE
										</th>
										{voyagesEnabled && (
											<th style={{ left: "240px" }}></th>
										)}
										{voaygeData
											.filter(
												(header) =>
													header.headerName !==
													"PORTCODEMSTID"
											)
											.map((header, index) => (
												<th
													key={index}
													style={{
														left:
															header.headerName ===
																"TIME STAMP"
																? voyagesEnabled
																	? "360px"
																	: "120px"
																: header.headerName ===
																	"EVENT TYPE"
																	? voyagesEnabled
																		? "560px"
																		: "320px"
																	: "",
													}}
												>
													{header.headerName}
												</th>
											))}
									</tr>
								</thead>
								<tbody>
									{filteredRowKeys.map((key, rowIndex) => {
										const rowStyle = getRowStyle(key); // Get the row style object with className

										return (
											<>
												{validationMessages[key] ? (
													<Tooltip
														title={
															<div
																style={{
																	whiteSpace: "pre-line",
																}}
															>
																{validationMessages[key]}
															</div>
														}
														placement="bottom"
														TransitionComponent={Zoom}
														followCursor={true}
														arrow
														style={{
															width: "100%",
															height: "100%",
														}}
														componentsProps={{
															tooltip: {
																sx: {
																	maxWidth: "1200px",
																	padding: "16px 20px",
																	fontSize: "14px",
																	lineHeight: 1.8,
																},
															},
														}}
													>														
														<tr
															id={`row-${key}`}
															className={`checkbox-selection 
		                                                    ${rowSelections[rowIndex] ? "selected-row" : ""} 
		                                                    ${activeRowKey === key ? "active-error-row" : ""}
	                                                        `}
															key={rowIndex}
															style={getValidationBg(validateTypes[key], rowIndex)}
														>
															{voyagesEnabled && (
																<td
																	style={{
																		maxWidth: "10px",
																		left: "0",
																	}}
																>
																	<Checkbox
																		size="small"
																		checked={rowSelections[rowIndex] || false}
																		onChange={handleRowCheckboxChange(rowIndex)}
																	/>
																</td>
															)}
															<td style={getWidthStyle("IS SAMPLE VOYAGE")}>
																<Checkbox
																	size="small"
																	checked={sampleVoyages[key] === "true"}
																	disabled={sampleVoyages[key] === "false"}
																/>
															</td>
															{voyagesEnabled && showbreadCrumb && (
																<td
																	style={{
																		maxWidth: "10px",
																		left: "240px",
																	}}
																>
																	<IconButton onClick={() => onPatchEditVoyage(key)}>
																		<EditIcon />
																	</IconButton>
																</td>
															)}

															{voaygeData
																.filter((record) => record.headerName !== "PORTCODEMSTID")
																.map((record, colIndex) => {
																	const cellValue = record.values[key] || "";

																	if (record.headerName === "SAILING IN ICE") {
																		const isSailingInIceChecked = cellValue === "true";
																		return (
																			<td key={colIndex}>
																				<Checkbox
																					size="small"
																					checked={isSailingInIceChecked}
																					disabled={!isSailingInIceChecked}
																				/>
																			</td>
																		);
																	}
																	if (record.headerName === "REMARKS") {
																		return (
																			<td
																				key={colIndex}
																				style={{
																					...getWidthStyle(record.headerName), // Keep any existing width styles

																					overflow: "hidden", // Hide overflowing content
																					textOverflow: "ellipsis", // Add ellipsis for truncated text
																					whiteSpace: "nowrap", // Prevent text wrapping
																					maxWidth: "200px", // Ensure there is a max width to trigger truncation
																				}}
																			>
																				{cellValue}
																			</td>
																		);
																	}
																	return (
																		<td key={colIndex} style={getWidthStyle(record.headerName)}>
																			{cellValue}
																		</td>
																	);
																})}
														</tr>
													</Tooltip>
												) : (
													<tr
														id={`row-${key}`}
														className={`checkbox-selection 
		                                                ${rowSelections[rowIndex] ? "selected-row" : ""} 
		                                                ${activeRowKey === key ? "active-error-row" : ""}
	                                                   `}
														key={rowIndex}
														style={getValidationBg(validateTypes[key], rowIndex)}
													>
														{voyagesEnabled && (
															<td
																style={{
																	maxWidth: "10px",
																	left: "0",
																}}
															>
																<Checkbox
																	size="small"
																	checked={rowSelections[rowIndex] || false}
																	onChange={handleRowCheckboxChange(rowIndex)}
																/>
															</td>
														)}
														<td style={getWidthStyle("IS SAMPLE VOYAGE")}>
															<Checkbox
																size="small"
																checked={sampleVoyages[key] === "true"}
																disabled={sampleVoyages[key] === "false"}
															/>
														</td>
														{voyagesEnabled && showbreadCrumb && (
															<td
																style={{
																	maxWidth: "10px",
																	background: "red",
																	left: "240px",
																	zIndex: "675 !important",
																}}
															>
																<IconButton onClick={() => onPatchEditVoyage(key)}>
																	<EditIcon />
																</IconButton>
															</td>
														)}

														{voaygeData
															.filter((record) => record.headerName !== "PORTCODEMSTID")
															.map((record, colIndex) => {
																const cellValue = record.values[key] || "";

																if (record.headerName === "SAILING IN ICE") {
																	const isSailingInIceChecked = cellValue === "true";
																	return (
																		<td key={colIndex}>
																			<Checkbox
																				size="small"
																				checked={isSailingInIceChecked}
																				disabled={!isSailingInIceChecked}
																			/>
																		</td>
																	);
																}
																if (record.headerName === "REMARKS") {
																	return (
																		<td
																			key={colIndex}
																			style={{
																				...getWidthStyle(record.headerName), // Keep any existing width styles

																				overflow: "hidden", // Hide overflowing content
																				textOverflow: "ellipsis", // Add ellipsis for truncated text
																				whiteSpace: "nowrap", // Prevent text wrapping
																				maxWidth: "200px", // Ensure there is a max width to trigger truncation
																			}}
																		>
																			{cellValue}
																		</td>
																	);
																}
																return (
																	<td key={colIndex} style={getWidthStyle(record.headerName, colIndex)}>
																		{cellValue}
																	</td>
																);
															})}
													</tr>
												)}
											</>
										);
										})}
								</tbody>

								<tfoot>
									<tr>
										{voyagesEnabled && (
											<td
												colSpan={1}
												style={{ left: "0" }}
											></td>
										)}
										<td
											style={{
												left: voyagesEnabled
													? "120px"
													: "0",
											}}
										></td>
										{voyagesEnabled && (
											<td
												style={{ left: "240px" }}
												colSpan={1}
											></td>
										)}
										{footerData
											.filter(
												(_, index) =>
													voaygeData[index]
														?.headerName !==
													"PORTCODEMSTID"
											)
											.map((footerValue, index) => (
												<td
													style={{
														fontWeight: "bold",
														left:
															voaygeData[index]
																?.headerName ===
																"TIME STAMP"
																? voyagesEnabled
																	? "360px"
																	: "120px"
																: voaygeData[
																	index
																]
																	?.headerName ===
																	"EVENT TYPE"
																	? voyagesEnabled
																		? "560px"
																		: "320px"
																	: "",
													}}
													key={index}
												>
													{footerValue}
												</td>
											))}
									</tr>
								</tfoot>
							</table>
						</div>
					</div>

					{/* Loading overlay */}
					{pendingLoads > 0 && (
						<div
							className="voyage-table-loading-overlay"
							role="status"
							aria-label="Loading voyages"
						>
							<Box className="voyage-table-loading-content">
								<CircularProgress size={32} thickness={5} />
							</Box>
						</div>
					)}
				</div>

				{/* Confirmation modals container */}
				<div className="confirmation-modal-container">
					{/* Delete confirmation modal */}
					<Modal
						isOpen={voyageConfirmDelete}
						modalHeader
						padding={"70px"}
						headerHeight={"50px"}
						showFooter
						onCloseClickListener={() => {
							setVoyageConfirmDelete(false);
						}}
						footer={
							<div className="d-flex justify-content-end fixed-action-btns p-4">
								<Button
									className="delete-btn"
									onClick={() => {
										const allRowIds = Object.keys(
											voaygeData.reduce((acc, column) => {
												return {
													...acc,
													...column.values,
												};
											}, {})
										)
											.map((key) => {
												const id = key.split("|")[1];
												return id ? Number(id) : null;
											})
											.filter(Boolean);
										const uniqueRowIds = allRowIds.filter(
											(id) => id !== 0
										);
										const selectedRows =
											uniqueRowIds.filter(
												(id, index) =>
													rowSelections[index]
											);
										const idsToDelete = uniqueRowIds.filter(
											(id) => selectedRows.includes(id)
										);
										if (idsToDelete.length > 0) {
											handleDeletingVoyageRecord(
												idsToDelete
											);
										}
									}}
									primaryColor={COLORS.RED.DARK}
									primaryBackgroundColor={COLORS.RED.LIGHT}
								>
									Delete
								</Button>

								<Button
									className="modal-cancel-btn"
									onClick={() => {
										setVoyageConfirmDelete(false);
									}}
								>
									Cancel
								</Button>
							</div>
						}
					>
						<div>Are you sure you want to delete the Voyage?</div>
					</Modal>

					{/* End-of-year confirmation modal */}
					<Modal
						isOpen={openFullYearModal}
						modalHeader
						padding={"70px"}
						headerHeight={"50px"}
						showFooter
						onCloseClickListener={handleFullYearModalClose}
						footer={
							<div className="d-flex justify-content-end fixed-action-btns p-4">
								<Button
									className="delete-btn"
									onClick={() => {
										sendValidateApiRequest(true);
										handleFullYearModalClose();
									}}
									primaryColor={COLORS.RED.DARK}
									primaryBackgroundColor={COLORS.RED.LIGHT}
								>
									Yes
								</Button>
								<Button
									className="cancel-btn"
									onClick={() => {
										sendValidateApiRequest(true);
										handleFullYearModalClose();
									}}
								>
									No
								</Button>
							</div>
						}
					>
						<div>
							Is this an End-of-Year submission (i.e. full year
							data)?
						</div>
					</Modal>
				</div>

				{/* Global errors modal */}
				<AbsModal
					isOpen={isGenericErrorsModalOpen}
					title="Global Error(s) "
					onClose={setIsGenericErrorsModalOpen}
					close={toggleGenericErrors}
				>
					{isGenericErrorsModalOpen && (
						<div className="generic-errors-wrapper">
							<label className="generic-errors-header">
								Error(s):{" "}
							</label>

							<ul className="error-list-wrapper">
								{globalErrors &&
									globalErrors.map((item) => {
										return <li>{item}</li>;
									})}
							</ul>
							<Button
								className="generic-errors-close-btn"
								variant="secondary"
								onClick={toggleGenericErrors}
							>
								Close
							</Button>
						</div>
					)}
				</AbsModal>

				{/* Import Additional Data modal */}
				<div className="import-additonal-request-form">
					<AbsModal
						isOpen={isImportAdditionalDataOpen}
						title="Import Additional Data"
						onClose={setIsImportAdditionalDataOpen}
						close={toggleImportAdditionalData}
					>
						{isImportAdditionalDataOpen && (
							<ImportAdditionalDataForm
								cancelImportReport={toggleImportAdditionalData}
								reportMstId={reportDetails?.ReportMstId}
								disableBtn={disableBtn}
								setDisableBtn={setDisableBtn}
							/>
						)}
					</AbsModal>
				</div>

				{/* Add/Edit Voyage modal */}
				<div className="voyage-modal">
					<AbsModal
						isOpen={isCorrectionReqModalOpen}
						title={`${type} ${VOYAGE_HEADER}`}
						onClose={setIsCorrectionReqModalOpen}
						close={closeForm}
					>
						{isCorrectionReqModalOpen && (
							isVoyageFormLoading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									minHeight="300px"
								>
									<CircularProgress size={32} />
								</Box>
							) : (

								<VoyageForm
									voyage_sections_data={voyageFields}
									editVoyage={editVoyage}
									type={type}
									onRefetchVoyageAndMisStateMentErrs={
										refetchVoyagesAndMisStatementErrors
									}
									onClose={closeForm}
									disableBtn={disableBtn}
									setDisableBtn={setDisableBtn}
								/>
							)
						)}
					</AbsModal>
				</div>
			</div>
		</>
	);
};

export default Voyage;
